import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Gauge } from 'lucide-react';
import type { AtsScoreResponse, AtsScoreBreakdown } from '../../types';

interface AtsScoreGaugeProps {
  score: number;
  breakdown?: AtsScoreBreakdown;
  loading?: boolean;
  compact?: boolean;
}

const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const getScoreColor = (val: number) => {
  if (val >= 75) return '#10B981';
  if (val >= 50) return '#F59E0B';
  return '#EF4444';
};

const getGrade = (val: number) => {
  if (val >= 90) return 'A+';
  if (val >= 80) return 'A';
  if (val >= 70) return 'B';
  if (val >= 60) return 'C';
  if (val >= 50) return 'D';
  return 'F';
};

const AnimatedNumber = ({ value, color }: { value: number; color: string }) => {
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    const duration = 700;
    const steps = 28;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayed(value);
        clearInterval(timer);
      } else {
        setDisplayed(Math.round(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className="text-4xl font-bold leading-none" style={{ color }}>
      {displayed}
    </span>
  );
};

const AtsScoreGauge: React.FC<AtsScoreGaugeProps> = ({ score, breakdown, loading, compact }) => {
  const color = getScoreColor(score);
  const offset = CIRCUMFERENCE * (1 - Math.min(100, Math.max(0, score)) / 100);

  const breakdownRows: Array<{ label: string; value: number; max: number }> = breakdown
    ? [
        { label: 'Contact', value: breakdown.contact, max: 20 },
        { label: 'Sections', value: breakdown.sections, max: 30 },
        { label: 'Impact', value: breakdown.impact, max: 25 },
        { label: 'JD Match', value: breakdown.jd_match, max: 25 },
      ]
    : [];

  return (
    <div className="bg-slate-900 p-4 rounded-lg">
      <div className={`flex items-center ${compact ? 'gap-3' : 'gap-4'}`}>
        <div className={`relative ${compact ? 'w-20 h-20' : 'w-28 h-28'} shrink-0`}>
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="#1E293B"
              strokeWidth="8"
            />
            <motion.circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              initial={{ strokeDashoffset: CIRCUMFERENCE }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
          </svg>
          <div className={`absolute inset-0 flex flex-col items-center justify-center ${compact ? 'gap-0' : 'gap-0.5'}`}>
            <span className={`${compact ? 'text-xl' : 'text-3xl'} font-bold leading-none text-white`}>
              {score}
            </span>
            <span className="text-[10px] text-slate-400 leading-none mt-0.5">/100</span>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Gauge className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-400`} />
            <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-semibold text-slate-300 tracking-wider uppercase`}>
              ATS Score
            </span>
          </div>
          <AnimatedNumber value={score} color={color} />
          <div className={`${compact ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} font-medium text-slate-400`}>
            {loading ? 'Scoring...' : `Grade ${getGrade(score)}`}
          </div>
        </div>
      </div>

      {breakdownRows.length > 0 && (
        <div className={`${compact ? 'mt-3 space-y-1.5' : 'mt-4 space-y-2'}`}>
          {breakdownRows.map((row) => {
            const rowColor = getScoreColor((row.value / row.max) * 100);
            return (
              <div key={row.label} className="flex items-center gap-2">
                <span className={`${compact ? 'text-[10px] w-14' : 'text-xs w-16'} text-slate-400 font-medium shrink-0`}>
                  {row.label}
                </span>
                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, (row.value / row.max) * 100)}%` }}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                    style={{ backgroundColor: rowColor }}
                  />
                </div>
                <span className={`${compact ? 'text-[10px] w-8' : 'text-xs w-9'} text-slate-300 font-semibold text-right shrink-0`}>
                  {row.value}/{row.max}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AtsScoreGauge;
export type { AtsScoreGaugeProps, AtsScoreResponse };
